import ApiClient from '../ApiClient';

const appId = process.env.REACT_APP_DEFAULT_APP_ID;

export const TEMPLATE_CREATOR_TYPES = {
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
};

const mapTemplate = (t) => ({
  id: t.id,
  name: t.name,
  templateBody: t.templateBody,
  templateDetails: t.templateDetails,
  templateCategory: t.templateCategory,
  templateImage: t.templateImage,
  active: t.isActive ?? t.active ?? true,
  createdOn: t.createdOn || t.createdDate || null,
  creatorType: t.creatorType || null,
});

const applyClientQuery = (items, { search, sortBy, sortOrder, pageNumber, pageSize }) => {
  let result = [...items];

  if (search?.trim()) {
    const query = search.trim().toLowerCase();
    result = result.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.templateCategory?.toLowerCase().includes(query),
    );
  }

  result.sort((a, b) => {
    const aVal = String(a[sortBy] ?? '').toLowerCase();
    const bVal = String(b[sortBy] ?? '').toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalCount = result.length;
  const start = (pageNumber - 1) * pageSize;

  return {
    items: result.slice(start, start + pageSize),
    totalCount,
    pageNumber,
    pageSize,
  };
};

const normalizeTemplateListResponse = (response, queryParams) => {
  const payload = response?.data;

  if (!payload?.success) {
    return {
      items: [],
      totalCount: 0,
      pageNumber: queryParams.pageNumber,
      pageSize: queryParams.pageSize,
    };
  }

  const data = payload.data;

  if (data && !Array.isArray(data) && Array.isArray(data.items)) {
    return {
      items: data.items.map(mapTemplate),
      totalCount: Number(data.totalCount) || 0,
      pageNumber: Number(data.pageNumber) || queryParams.pageNumber,
      pageSize: Number(data.pageSize) || queryParams.pageSize,
    };
  }

  const allItems = (Array.isArray(data) ? data : []).map(mapTemplate);
  return applyClientQuery(allItems, queryParams);
};

const TemplateService = {
  async fetchTemplates() {
    if (!appId) throw new Error('appId is required');
    const resp = await ApiClient.get(`/Template/getall-templates`);
    return resp;
  },

  async fetchTemplatesByCreatorType({
    creatorType,
    pageNumber = 1,
    pageSize = 10,
    search = '',
    sortBy = 'name',
    sortOrder = 'asc',
  }) {
    if (!appId) throw new Error('appId is required');

    const queryParams = { creatorType, pageNumber, pageSize, search, sortBy, sortOrder };
    const query = new URLSearchParams({
      creatorType,
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      sortBy,
      sortOrder,
    });

    if (search?.trim()) {
      query.append('search', search.trim());
    }

    const resp = await ApiClient.get(`/Template/getall-templates?${query.toString()}`);
    return normalizeTemplateListResponse(resp, queryParams);
  },

  async fetchSuperAdminTemplates() {
    if (!appId) throw new Error('appId is required');

    const endpoints = [
      '/Template/getall-sample-templates',
      '/Template/get-all-sample-templates',
    ];

    for (const endpoint of endpoints) {
      try {
        const resp = await ApiClient.get(endpoint);
        return resp;
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          continue;
        }
        throw error;
      }
    }

    return { data: { success: true, data: [] } };
  },

  async fetchSuperAdminTemplatesList({
    pageNumber = 1,
    pageSize = 10,
    search = '',
    sortBy = 'name',
    sortOrder = 'asc',
  } = {}) {
    const queryParams = { pageNumber, pageSize, search, sortBy, sortOrder };
    const resp = await this.fetchSuperAdminTemplates();
    return normalizeTemplateListResponse(resp, queryParams);
  },

  async createTemplate(formData) {
    if (!appId) throw new Error('appId is required');
    const resp = await ApiClient.post('/Template/create-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp;
  },

  async updateTemplate(formData) {
    if (!appId) throw new Error('appId is required');
    const resp = await ApiClient.put('/Template/update-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp;
  },

  async deleteTemplate(id) {
    if (!appId) throw new Error('appId is required');
    const resp = await ApiClient.delete(`/Template/delete-template/${id}`);
    return resp;
  },

  async createEventImage(formData) {
    if (!appId) throw new Error('appId is required');
    const resp = await ApiClient.post('/Template/template-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp;
  },
};

export default TemplateService;
